import { DetailsInformation } from 'common/components/DetailsInformation/DetailsInformation';
import {
  getFileNameFromUrl,
  optionParseInt,
  redirectToEmail,
  redirectToMapsLocation,
  redirectToPhone,
} from 'common/helpers';
import { Location } from 'common/components/icons/Location';
import { Warning } from 'common/components/icons/Warning';
import { useTranslation } from 'common/hooks/useTranslation';

import { FC, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { G, pipe } from '@mobily/ts-belt';
import { Button, CircularProgress, Grid, Typography } from '@mui/material';
import { CacheDataButton } from 'app/installation/components/Installation/CacheDataButton';
import { useActiveWorkSession } from 'app/installation/hooks/useActiveWorkSession';

import { RoutesParamsMap } from '../../routes/Route';
import { createSignedLinkToDownloadPropertyFile } from '../api';
import { useProperty } from '../hooks/useProperty';
import { getFullAddress } from '../Property';
import * as styles from './PropertyInformations.styles';
import { useFiberChecklist } from '../hooks/useFiberChecklist';
import { PropertyChecklist } from './PropertyChecklist';

export const PropertyInformations: FC = () => {
  const params = useParams<RoutesParamsMap['property/:id']>();
  const parsedId = useMemo(() => optionParseInt(params.id), [params]);

  const { t } = useTranslation();

  const { data, isLoading, isError } = useProperty(parsedId);

  const [showChecklist, setShowChecklist] = useState(false);
  const { isInstallationOngoing } = useActiveWorkSession(parsedId);

  const {
    isLoading: isLoadingChecklist,
    isError: isErrorChecklist,
    isErrorUpdatePropertyFiberChecklist,
    isErrorCreatePropertyFiberChecklist,
  } = useFiberChecklist(parsedId);

  const textNA = useMemo(() => t('notAvailable.general'), [t]);

  if (isLoading) return <CircularProgress sx={styles.loader} />;

  if (
    isError ||
    isErrorChecklist ||
    isErrorUpdatePropertyFiberChecklist ||
    isErrorCreatePropertyFiberChecklist ||
    G.isNullable(data)
  )
    return (
      <Grid container sx={styles.warningContainer}>
        <Warning />
        <Typography sx={styles.warningInfo}>{t('error.generalMessage')}</Typography>
      </Grid>
    );

  return (
    <>
      <PropertyChecklist id={parsedId} show={showChecklist} onShow={setShowChecklist} />
      <Grid container sx={styles.container}>
        <Grid container sx={styles.infoContainer}>
          {data.contacts.property
            .filter((person) => person.hideInInstaller !== true)
            .map((person) => (
              <Grid
                key={`${person.name}-${person.email}`}
                data-cy="property-person-contact"
                container
                sx={styles.infoSection}
              >
                <DetailsInformation label={t('property.contactPersonProperty')} value={person.name ?? textNA} />
                <DetailsInformation
                  label={t('property.propertyEmail')}
                  value={person.email ?? textNA}
                  url={person.email ? redirectToEmail(person.email) : undefined}
                />
                <DetailsInformation
                  label={t('property.propertyMobile')}
                  value={person.phone ?? textNA}
                  url={person.phone ? redirectToPhone(person.phone) : undefined}
                />
                <DetailsInformation label={t('property.propertyRole')} value={person.role ?? textNA} />
              </Grid>
            ))}
          <Grid data-cy="property-CF-contact" container sx={styles.infoSection}>
            <DetailsInformation label={t('property.contactPersonCF')} value={data.contacts.CF.name ?? textNA} />
            <DetailsInformation
              label={t('property.CFEmail')}
              value={data.contacts.CF.email ?? textNA}
              url={data.contacts.CF.email ? redirectToEmail(data.contacts.CF.email) : undefined}
            />
            <DetailsInformation
              label={t('property.CFMobile')}
              value={data.contacts.CF.phone ?? textNA}
              url={data.contacts.CF.phone ? redirectToPhone(data.contacts.CF.phone) : undefined}
            />
            <DetailsInformation label={t('property.CFRole')} value={data.contacts.CF.role ?? textNA} />
          </Grid>
          <Grid data-cy="property-address" container sx={styles.infoSection}>
            <Typography sx={styles.infoLabel}>{t('properties.address.label')}</Typography>
            <Button
              sx={styles.infoLinkValue}
              variant="text"
              onClick={() => pipe(data, getFullAddress, redirectToMapsLocation)}
            >
              {data.address}
              <Location />
            </Button>
          </Grid>
          {data.attachments.length > 0 && (
            <Grid container sx={styles.infoSection}>
              <Typography sx={styles.infoLabel}>{t('property.documents')}</Typography>
              {data.attachments.map((attachment) => (
                <Button
                  key={attachment}
                  sx={styles.infoLinkValue}
                  variant="text"
                  href={attachment}
                  onClick={async (e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    const { query } = await createSignedLinkToDownloadPropertyFile(
                      pipe(attachment, getFileNameFromUrl, encodeURIComponent),
                    );

                    window.location.href = `${attachment}?${query}`;
                  }}
                  download={getFileNameFromUrl(attachment)}
                >
                  {getFileNameFromUrl(attachment)}
                </Button>
              ))}
            </Grid>
          )}
          <Grid container sx={styles.infoSection}>
            <Button
              variant="contained"
              disabled={isLoadingChecklist}
              color="primary"
              onClick={() => setShowChecklist(true)}
            >
              {t('checklist.title')}
            </Button>
            {isInstallationOngoing && <CacheDataButton propertyId={parsedId} />}
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
